import React, {useCallback, useEffect, useMemo, useState} from "react";
import Dialog from "../../../../../../components/base/dialog";
import {Col, Row} from "reactstrap";
import {alpha, Divider, SvgIcon, useTheme} from "@mui/material";
import Button from "../../../../../../components/base/button/button";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ReactComponent as Copy} from '../../../../../../../assets/images/copy.svg'
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import CopyToClipboard from "../../../../../../components/base/copy-to-clipboard";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import DateUtils from "../../../../../../../core/services/date-utils";
import {Link} from "react-router-dom";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {usePartFormQualificationGate} from "../../../../../../features/part-form-qualification";
import {
    createQuoteTarget,
    getQuoteModalPendingKey,
    SuggestiveSellingSectionPending,
    useSuggestiveSelling,
} from "../../../../../../features/suggestive-selling";


const AddToQuoteModal = ({open, setOpen, quantity, partId, partNumber, warehouseCode, customerId, customerName, searchToken, resetValues}) => {
    const operatorAccessLevels = useAccessLevels();
    const {ensureQualified, GateDialogs} = usePartFormQualificationGate();
    const {offerAfterAdd, clearSuggestiveDismissalsForTarget} = useSuggestiveSelling();
    const [mostRecent, setMostRecent] = useState();
    const [loadingMostRecent, setLoadingMostRecent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [quote, setQuote] = useState(null);
    const isMounted = useIsMounted();
    const theme = useTheme();

    const newTabUrl = useMemo(() => routeFunctions.customer.sales.quotes.single(customerId ?? 0, quote?.internalId ?? 0), [quote?.internalId, customerId]);
    const quoteModalPendingKey = useMemo(() => getQuoteModalPendingKey(partId), [partId]);

    /**
     * Gets the user last quote from api/
     */
    const getUserLastQuote = useCallback(async () => {
        setLoadingMostRecent(true);
        const response = await Api.getCustomerLastQuote(customerId);
        if (!isMounted())
            return
        const quoteId = response?.data?.id;
        if (!!quoteId) {
            const response = await Api.getQuoteInformationForCustomer(customerId, quoteId);
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                setMostRecent(response.data);
            }
        }
        if (isMounted()) {
            setLoadingMostRecent(false);
        }
    }, [customerId, isMounted]);

    /**
     * if modal closes, set the quote to null to refresh the process.
     */
    useEffect(() => {
        if (!open) {
            setQuote(null);
            return;
        }
        clearSuggestiveDismissalsForTarget('quote');
        void getUserLastQuote();
    }, [clearSuggestiveDismissalsForTarget, getUserLastQuote, open])


    /**
     * Binds the customer to the search token
     * @type {(function(*): Promise<void>)}
     */
    const bindOrderToSearchToken = useCallback(async (quoteNumber) => {
        if (!searchToken)
            return;
        await Api.connectNexusSearchToOrder({
            searchToken: searchToken,
            quoteNumber: quoteNumber,
        });
    }, [searchToken])

    /**
     * Creates a new quote and returns the newly created quote.
     */
    const createNewQuote = useCallback(async () => {
        setLoading(true);
        const canProceed = await ensureQualified({
            partId,
            customerId,
            partLabel: partNumber,
        });
        if (!isMounted())
            return;
        if (!canProceed) {
            setLoading(false);
            return;
        }
        const forApi = {
            customerId: customerId,
            parts: [{
                partNumber: partNumber,
                qty: quantity,
                warehouseCode: warehouseCode,
            }],
        };
        const response = await Api.createNewQuoteV2(forApi, true);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            resetValues?.()
            void bindOrderToSearchToken(response.data.orderNumber);
            await offerAfterAdd({
                sourcePartId: partId,
                sourcePartNumber: partNumber,
                sourceQuantity: quantity,
                pendingKey: quoteModalPendingKey,
                target: createQuoteTarget({
                    customerId,
                    orderId: response.data.internalId,
                    warehouseCode,
                    getOrderItems: () => [{
                        partNo: partNumber,
                        orderQty: String(quantity),
                    }],
                    onOrderUpdated: () => void 0,
                    salesOptions: {
                        allowDuplicatePartNumbersInOrders: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders,
                        manageWarehousesInQuote: operatorAccessLevels.packages.sales.manageWarehousesInQuote,
                    },
                }),
            });
            if (!isMounted())
                return;
            setQuote(response.data);
        }
        if (!isMounted())
            return;
        setLoading(false);
    }, [bindOrderToSearchToken, customerId, ensureQualified, isMounted, offerAfterAdd, operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders, operatorAccessLevels.packages.sales.manageWarehousesInQuote, partId, partNumber, quantity, quoteModalPendingKey, resetValues, warehouseCode]);

    /**
     * Adds the part to the most recent quote and sets the most recent as the quote.
     */
    const addToMostRecentQuote = useCallback(async () => {
        setLoading(true);
        const canProceed = await ensureQualified({
            partId,
            customerId,
            partLabel: partNumber,
        });
        if (!isMounted())
            return;
        if (!canProceed) {
            setLoading(false);
            return;
        }
        const existingItem = mostRecent?.items?.find(e => e.partNo === partNumber);
        const existingItemQty = (Number(existingItem?.orderQty ?? '0'))
        const forApi = {
            customerId: customerId,
            orderId: mostRecent?.id,
            PartCountChangedList: [
                {
                    partNo: partNumber,
                    finalCount: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders
                        ? 1
                        : existingItemQty + (1 * (existingItemQty < 0) ? -quantity : quantity),
                    unitPrice: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders ? null : existingItem?.unitPriceBeforeDiscount,
                    ...(operatorAccessLevels.packages.sales.manageWarehousesInQuote && {
                        whs: warehouseCode,
                    }),
                    partDescription: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders ? null : existingItem?.description,
                }
            ]
        };
        const response = await Api.updateQuoteItems(forApi)
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            resetValues?.()
            void bindOrderToSearchToken(mostRecent?.orderNo);
            await offerAfterAdd({
                sourcePartId: partId,
                sourcePartNumber: partNumber,
                sourceQuantity: quantity,
                pendingKey: quoteModalPendingKey,
                target: createQuoteTarget({
                    customerId,
                    orderId: mostRecent?.id,
                    warehouseCode,
                    getOrderItems: () => mostRecent?.items ?? [],
                    onOrderUpdated: setMostRecent,
                    salesOptions: {
                        allowDuplicatePartNumbersInOrders: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders,
                        manageWarehousesInQuote: operatorAccessLevels.packages.sales.manageWarehousesInQuote,
                    },
                }),
            });
            if (!isMounted())
                return;
            setQuote({
                orderNumber: mostRecent?.orderNo,
                internalId: mostRecent?.id,
            });
        }
        if (!isMounted())
            return;
        setLoading(false);
    }, [mostRecent?.items, mostRecent?.id, mostRecent?.orderNo, customerId, ensureQualified, offerAfterAdd, partId, partNumber, operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders, operatorAccessLevels.packages.sales.manageWarehousesInQuote, quantity, quoteModalPendingKey, warehouseCode, isMounted, resetValues, bindOrderToSearchToken])

    return (
        <>
            {GateDialogs}
            <Dialog
                open={open}
                setOpen={setOpen}
                loading={loading}
                className={'add-quote-dialog'}
            >
                <Row className='no-gutters justify-content-center align-items-start w-100'>
                    <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                        <p className={'title'}>
                            {
                                !quote
                                    ? `Add to ${customerName}'s Quote`
                                    : `Successfully Added to ${customerName} Quote!`
                            }
                        </p>
                    </Row>
                    <Divider className='divider'/>
                    <div className='my-3 w-100'/>
                    <SuggestiveSellingSectionPending pendingKey={quoteModalPendingKey}/>
                    {
                        !quote
                            ? (
                                <Row className='no-gutters justify-content-center align-items-center w-100 mx-4'>
                                    <Col xs={12} className='mb-5 d-flex justify-content-center'>
                                        <Button
                                            onClick={createNewQuote}
                                            disabled={loading}
                                            color={'primary'}
                                            variant={'contained'}
                                            size={'medium'}
                                        >
                                            Create New Quote
                                        </Button>
                                    </Col>
                                    {
                                        loadingMostRecent
                                            ? (
                                                <div className='loading customer w-100 mb-4'>
                                                    <div/>
                                                </div>
                                            )
                                            : !!mostRecent && (
                                            <Col xs={12} className='mb-4 align-items-start'>
                                                <p className='most-recent-quote-label'>
                                                    Most Recent Quote:
                                                </p>
                                                <Row
                                                    className={`background-secondary-color-3 rounded-xxl no-gutters align-items-center justify-content-between px-3 py-3`}>
                                                    <Col xs={4}>
                                                        <p className='order-number'>
                                                            {mostRecent?.orderNo ?? '--'}
                                                        </p>
                                                    </Col>
                                                    <Col xs={4} className='text-center'>
                                                        <p className={'order-date'}>
                                                            {DateUtils.serverToLocal(mostRecent?.orderDate).format(DateUtils.dateFormat)}
                                                        </p>
                                                    </Col>
                                                    <Col xs={4}
                                                         className='row no-gutters align-items-center justify-content-end'>
                                                        <Button
                                                            disabled={loading}
                                                            variant={'outlined'}
                                                            color={'primary'}
                                                            onClick={addToMostRecentQuote}
                                                        >
                                                            {loading ? 'submitting...' : 'Add to This'}
                                                        </Button>
                                                    </Col>

                                                </Row>
                                            </Col>
                                        )
                                    }

                                </Row>
                            )
                            : (
                                <Row className='no-gutters justify-content-center align-items-center'>
                                    <Col className={`bordered mx-4 mb-5`}
                                         style={{backgroundImage: theme.palette['app-specific'].dashedBorder(theme.shape.borderRadius, alpha(theme.palette.secondary.main, 0.2))}}
                                    >
                                        <p className='text-center text-md font-weight-semi-bold'>{quote?.orderNumber ?? '--'}</p>
                                    </Col>
                                    <Col xs={12} className=' mb-5'>
                                        <p className='text-xs text-color-1 mb-2 mx-4'>Shareable Link:</p>
                                        <Row
                                            className={`background-secondary-color-3 no-gutters align-items-center justify-content-between px-3 py-2 mx-4`}>
                                            <p className='text-color-1 text-sm w-75'>
                                                {window.location.origin.concat(newTabUrl)}
                                            </p>
                                            <CopyToClipboard>
                                                {({copy}) =>
                                                    <IconButton
                                                        onPressed={() => copy(window.location.origin.concat(newTabUrl))}
                                                        color={'paper.secondary'}
                                                        className={'copy-to-clipboard-button'}
                                                    >
                                                        <SvgIcon className={'icon normal'} component={Copy}/>
                                                    </IconButton>
                                                }
                                            </CopyToClipboard>

                                        </Row>
                                    </Col>
                                    <div className='my-1 w-100'/>
                                    <Divider className='divider'/>
                                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                                        <Button
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => setOpen(false)}
                                            borderRadius={'0 8px 0 8px'}
                                        >
                                            Close
                                        </Button>
                                        <Button
                                            variant={'dialog'}
                                            color={'primary'}
                                            borderRadius={'8px 0 8px 0'}
                                            component={Link}
                                            to={newTabUrl}
                                            target={'_blank'}
                                        >
                                            Open in new Tab
                                        </Button>
                                    </Row>
                                </Row>
                            )
                    }
                </Row>
            </Dialog>
        </>
    );
}

export default AddToQuoteModal;
